import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { COMPANY, AREAS, BOOKING_URL } from "@/data/site";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/fleet", label: "Fleet" },
  { to: "/business", label: "Business" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setAreasOpen(false);
  }, [location.pathname]);

  return (
    <header data-testid="main-navbar" className="sticky top-0 inset-x-0 z-50">
      {/* slim ink top bar */}
      <div className="bg-ink text-white text-[13px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-9 flex items-center justify-between">
          <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-2 font-medium hover:text-brand-light transition-colors">
            <Phone className="h-3.5 w-3.5 text-brand-light" strokeWidth={2.25} /> {COMPANY.phone}
          </a>
          <span className="hidden sm:block text-white/60 tracking-wide">24/7 Licensed Private Hire · Gatwick & Surrey</span>
        </div>
      </div>

      <div className="bg-white border-b border-[#EAEBEA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-[92px] md:h-[104px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0">
            <img
              src="/images/clocktower-logo.png"
              alt="Clocktower Cars Gatwick — Licensed Private Hire & Airport Transfers"
              width="929"
              height="224"
              fetchpriority="high"
              className="h-14 md:h-[68px] lg:h-[76px] w-auto transition-transform duration-500 ease-out group-hover:scale-[1.02] drop-shadow-[0_2px_4px_rgba(0,0,0,0.06)]"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9 text-[15px] font-medium">
            {[{ to: "/", label: "Home" }, { to: "/fleet", label: "Fleet" }, { to: "/business", label: "Business" }, { to: "/blog", label: "Blog" }].map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}`} className="text-ink/80 hover:text-ink transition-colors">
                {l.label}
              </Link>
            ))}
            <div className="relative" onMouseEnter={() => setAreasOpen(true)} onMouseLeave={() => setAreasOpen(false)}>
              <button data-testid="nav-areas-trigger" className="flex items-center gap-1 text-ink/80 hover:text-ink transition-colors">
                Areas <ChevronDown className={`h-4 w-4 transition-transform ${areasOpen ? "rotate-180" : ""}`} />
              </button>
              {areasOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                  <div className="w-[340px] bg-white border border-[#EAEBEA] rounded-2xl shadow-[0_24px_50px_-12px_rgba(0,0,0,0.15)] p-2 grid grid-cols-2 gap-0.5">
                    {AREAS.map((a) => (
                      <Link key={a.slug} to={`/areas/${a.slug}`} data-testid={`nav-area-${a.slug}`} className="block px-4 py-2.5 text-sm text-ink/70 hover:bg-surface hover:text-brand rounded-lg transition-colors">
                        {a.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link to="/contact" data-testid="nav-contact" className="text-ink/80 hover:text-ink transition-colors">Contact</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={BOOKING_URL} data-testid="nav-book-btn" className="bg-brand hover:bg-brand-light text-white px-7 py-3 rounded-full font-bold transition-all duration-300 hover:shadow-[0_8px_20px_rgba(31,157,42,0.3)] active:scale-95">
              Book Now
            </a>
          </div>

          <button data-testid="mobile-menu-toggle" className="lg:hidden text-ink" onClick={() => setOpen((o) => !o)}>
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-b border-[#EAEBEA] px-6 py-6 space-y-1">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} className="block py-3 text-ink font-medium border-b border-[#F0F0F0]">
              {l.label}
            </Link>
          ))}
          <div className="pt-3">
            <p className="text-xs uppercase tracking-widest text-ink/40 mb-2 font-bold">Areas</p>
            <div className="grid grid-cols-2 gap-1">
              {AREAS.map((a) => (
                <Link key={a.slug} to={`/areas/${a.slug}`} className="py-2 text-ink/70 hover:text-brand text-sm">
                  {a.name}
                </Link>
              ))}
            </div>
          </div>
          <a href={BOOKING_URL} data-testid="mobile-book-btn" className="mt-4 flex items-center justify-center gap-2 bg-brand text-white px-5 py-3 rounded-full font-bold">
            Book Now
          </a>
          <a href={`tel:${COMPANY.phoneRaw}`} className="mt-3 flex items-center justify-center gap-2 bg-ink text-white px-5 py-3 rounded-full font-bold">
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
        </div>
      )}
    </header>
  );
};
